import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  TemplateRef,
  signal,
  effect,
  NgZone,
  input,
  contentChild,
  viewChild,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';

import { MdwSwapTemplateDirective } from './swapper-template';

@Component({
  selector: 'mdw-swap-animate',
  templateUrl: './swapper.html',
  styleUrls: ['./swapper.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule],
})
export class MdwSwapAnimateComponent implements AfterViewInit {
  private readonly zone = inject(NgZone);

  /** Change this input to trigger the animation. It will be provided to your template as `$implicit`. */
  public model = input.required<unknown>();
  public fadeMs = input<number>(140);
  public resizeMs = input<number>(200);
  public timing = input<string>('ease');
  public fillHeight = input<boolean>(false);

  public swapTemplate = contentChild(MdwSwapTemplateDirective, { read: TemplateRef<unknown> });
  public hostRef = viewChild('host', { read: ElementRef<HTMLElement> });
  public contentRef = viewChild('content', { read: ElementRef<HTMLElement> });

  protected _void = Symbol('void');
  renderedModel = signal<unknown>(this._void);
  phase = signal<'idle' | 'out' | 'resize' | 'in'>('idle');

  private firstRenderDone = false;

  public constructor() {
    effect(() => {
      const newValue = this.model();

      if (!this.firstRenderDone) {
        return;
      }

      this.runSequence(newValue);
    });
  }

  public ngAfterViewInit(): void {
    // apply timing vars (supports per-instance overrides)
    const host = this.hostRef()?.nativeElement;

    if (!host) {
      return;
    }

    host.style.setProperty('--fade-duration', `${this.fadeMs()}ms`);
    host.style.setProperty('--resize-duration', `${this.resizeMs()}ms`);
    host.style.setProperty('--timing', this.timing());

    // initial render without animation
    this.renderedModel.set(this.model());
    this.firstRenderDone = true;
    // ensure max-height tracks initial content
    queueMicrotask(() => this.syncMaxHeight());
  }

  // ---- sequence orchestration ----
  private async runSequence(nextModel: unknown) {
    const hostRef = this.hostRef();
    const contentRef = this.contentRef();

    if (!hostRef || !contentRef) {
      // not ready yet
      return;
    }

    const host = hostRef.nativeElement;
    const content = contentRef.nativeElement;

    host.classList.add('animating');

    // 1) fade out
    await this.fadeTo(content, 0);

    // 2) lock current height (in px)
    const startH = Math.ceil(host.getBoundingClientRect().height);
    host.style.height = `${startH}px`;

    // 3) swap while invisible
    this.renderedModel.set(nextModel);

    // Let Angular render + perform layout (double RAF helps in complex trees)
    await this.nextFrame();
    await this.nextFrame();

    // 4) measure the *content’s* new height (not the host!)
    const targetH = Math.ceil(content.getBoundingClientRect().height);
    // Guard: if equal, skip the height transition
    if (targetH !== startH) {
      host.classList.add('resizing');
      // ensure start height is committed
      void host.offsetWidth;
      // trigger transition
      await this.transitionHeight(host, targetH, this.resizeMs());
      host.classList.remove('resizing');
    }

    // 5) cleanup height
    host.style.height = '';

    // 6) fade in
    await this.fadeTo(content, 1);

    host.classList.remove('animating');
  }

  /** Opacity transition helper */
  private fadeTo(el: HTMLElement, to: 0 | 1): Promise<void> {
    return new Promise<void>(res => {
      const onEnd = (e: TransitionEvent) => {
        if (e.target !== el || e.propertyName !== 'opacity') {
          return;
        }

        el.removeEventListener('transitionend', onEnd);
        res();
      };
      el.addEventListener('transitionend', onEnd);
      // If duration == 0 or element is detached, fallback
      const fallback = setTimeout(() => {
        el.removeEventListener('transitionend', onEnd);
        res();
      }, this.fadeMs() + 50);

      // Force current style, then set target
      void el.offsetWidth;
      el.style.opacity = String(to);

      // If computed transition duration is 0, resolve quickly
      const dur = getComputedStyle(el).transitionDuration;
      if (!dur || dur === '0s') {
        clearTimeout(fallback);
        res();
      }
    });
  }

  /** Height transition helper */
  private transitionHeight(host: HTMLElement, target: number, fallbackMs: number): Promise<void> {
    return new Promise<void>(res => {
      const onEnd = (e: TransitionEvent) => {
        if (e.target !== host || e.propertyName !== 'height') {
          return;
        }

        host.removeEventListener('transitionend', onEnd);
        res();
      };
      host.addEventListener('transitionend', onEnd);

      // Kick the transition from current inline height → target
      host.style.height = `${target}px`;

      // Fallback in case of equal heights or 0-duration
      setTimeout(
        () => {
          host.removeEventListener('transitionend', onEnd);
          res();
        },
        Math.max(50, fallbackMs + 50),
      );
    });
  }

  private nextFrame(): Promise<void> {
    return new Promise(r => this.zone.runOutsideAngular(() => requestAnimationFrame(() => r())));
  }

  private syncMaxHeight() {
    const hostRef = this.hostRef();
    if (!hostRef) {
      return;
    }

    const host = hostRef.nativeElement;
    host.style.setProperty('--target-h', `${host.scrollHeight}px`);
  }
}
